import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faEnvelope,
  faGraduationCap,
  faSave,
  faTimes,
  faLock,
  faCamera,
  faCrown,
  faGem,
  faShieldAlt,
  faCircleNotch,
  faEye,
  faEyeSlash,
  faCalendarAlt,
  faCheckCircle,
  faBolt,
  faPenToSquare,
  faWandMagicSparkles,
  faFingerprint,
  faSignature,
  faIdCard,
  faArrowRight,
  faChevronLeft,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import ApiServices from "../Services/ApiServices";
import Loader from "../Shared/Loader";
import { getFullFileUrl } from "../../Helpers/UrlHelpers";
import { toastHelper } from "../../Helpers/toastHelper";
import extractErrorMessage from "../../Helpers/APIHelper";
import { useLanguage } from '../../contexts/LanguageContext';

const Profile = ({ profileData }) => {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [profileView, setProfileView] = useState(profileData || null);
  const [formData, setFormData] = useState({
    fullName: profileData?.teacher_name || "",
    email: profileData?.teacher_email || "",
    qualification: profileData?.teacher_qualification || "",
    password: "",
    profile_picture: null,
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState("info");

  useEffect(() => {
    setProfileView(profileData || null);
    setFormData({
      fullName: profileData?.teacher_name || "",
      email: profileData?.teacher_email || "",
      qualification: profileData?.teacher_qualification || "",
      password: "",
      profile_picture: null,
    });
  }, [profileData]);

  const daysRemaining = profileView?.user_subscription_as_object?.days_remaining;
  const subscriptionType =
    profileView?.user_subscription_as_object?.plan_name || t('freePlan');

  const initials = useMemo(() => {
    const name = profileView?.teacher_name || "";
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return language === 'ar' ? "م" : "T";
    return parts.slice(0, 2).map(p => p[0]).join("");
  }, [profileView?.teacher_name]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFormData(prev => ({ ...prev, profile_picture: file }));
    const reader = new FileReader();
    reader.onloadend = () => setPreviewImage(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== "") {
          formDataToSend.append(key, formData[key]);
        }
      });
      await ApiServices.put("User/UpdateUserProfile", formDataToSend, true);
      setProfileView(prev => ({
        ...prev,
        teacher_name: formData.fullName,
        teacher_email: formData.email,
        teacher_qualification: formData.qualification,
      }));
      setIsEditing(false);
      setShowPassword(false);
      toastHelper.success(t('profileUpdateSuccess'));
    } catch (error) {
      toastHelper.error(extractErrorMessage(error) || t('somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  if (!profileView) return <div className="tp-loader"><Loader /></div>;

  return (
    <div className="tp-root" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="tp-container">

        {/* Left Column: Profile Overview */}
        <motion.aside
          className="tp-aside"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <div className="tp-card tp-card--identity">
            <div className="tp-card-header">
              <div className="tp-subscription-badge">
                <FontAwesomeIcon icon={faGem} />
                <span>{subscriptionType}</span>
              </div>
            </div>

            <div className="tp-avatar-box">
              <div className="tp-avatar-ring">
                <div className="tp-avatar">
                  {previewImage || profileView?.profile_picture ? (
                    <img src={previewImage || getFullFileUrl(profileView?.profile_picture)} alt="" />
                  ) : (
                    <div className="tp-avatar-fallback">{initials}</div>
                  )}
                  {isEditing && (
                    <label className="tp-avatar-edit">
                      <FontAwesomeIcon icon={faCamera} />
                      <input type="file" hidden onChange={handleImageChange} accept="image/*" />
                    </label>
                  )}
                </div>
              </div>
              <div className="tp-status-dot" />
            </div>

            <div className="tp-identity">
              <h2>{profileView?.teacher_name}</h2>
              <span className="tp-tag">
                <FontAwesomeIcon icon={faGraduationCap} />
                {t('certifiedTeacher')}
              </span>
            </div>

            <div className="tp-aside-actions">
              {!isEditing ? (
                <button className="tp-btn tp-btn--primary" onClick={() => setIsEditing(true)}>
                  <FontAwesomeIcon icon={faPenToSquare} />
                  <span>{t('editProfile')}</span>
                </button>
              ) : (
                <div className="tp-btn-group">
                  <button className="tp-btn tp-btn--success" onClick={handleSubmit} disabled={loading}>
                    <FontAwesomeIcon icon={loading ? faCircleNotch : faSave} spin={loading} />
                    <span>{t('save')}</span>
                  </button>
                  <button className="tp-btn tp-btn--ghost" onClick={() => setIsEditing(false)}>
                    <FontAwesomeIcon icon={faTimes} />
                    <span>{t('cancel')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="tp-card tp-card--stats">
            <div className="tp-stat-item">
              <div className="icon"><FontAwesomeIcon icon={faCheckCircle} /></div>
              <div className="info">
                <span className="label">{t('accountStatus')}</span>
                <span className="value text-success">{t('verifiedAndActive')}</span>
              </div>
            </div>
            <div className="tp-stat-item">
              <div className="icon text-amber"><FontAwesomeIcon icon={faCalendarAlt} /></div>
              <div className="info">
                <span className="label">{t('subscriptionEnd')}</span>
                <span className="value">{daysRemaining ? t('nDays').replace('{count}', daysRemaining) : t('noActiveSubscription')}</span>
              </div>
            </div>
          </div>
        </motion.aside>

        {/* Right Column: Details & Tabs */}
        <motion.main
          className="tp-main"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="tp-card tp-card--main">
            <div className="tp-tabs-header">
              <button
                className={`tp-tab-btn ${activeTab === 'info' ? 'is-active' : ''}`}
                onClick={() => setActiveTab('info')}
              >
                <FontAwesomeIcon icon={faIdCard} />
                <span>{t('basicInformation')}</span>
              </button>
              <button
                className={`tp-tab-btn ${activeTab === 'security' ? 'is-active' : ''}`}
                onClick={() => setActiveTab('security')}
              >
                <FontAwesomeIcon icon={faShieldAlt} />
                <span>{t('securityAndPrivacy')}</span>
              </button>
            </div>

            <div className="tp-card-body">
              <AnimatePresence mode="wait">
                {activeTab === 'info' && (
                  <motion.div
                    key="info"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="tp-form-grid"
                  >
                    {!isEditing ? (
                      <div className="tp-view-grid">
                        <InfoItem icon={faUser} label={t('fullName')} value={profileView?.teacher_name} />
                        <InfoItem icon={faEnvelope} label={t('email')} value={profileView?.teacher_email} />
                        <InfoItem icon={faGraduationCap} label={t('qualification')} value={profileView?.teacher_qualification} />
                        <InfoItem icon={faGem} label={t('subscriptionPlan')} value={subscriptionType} />
                      </div>
                    ) : (
                      <div className="tp-edit-form">
                        <div className="tp-field">
                          <label>{t('fullName')}</label>
                          <div className="tp-input-box">
                            <FontAwesomeIcon icon={faSignature} />
                            <input type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} />
                          </div>
                        </div>
                        <div className="tp-field">
                          <label>{t('email')}</label>
                          <div className="tp-input-box">
                            <FontAwesomeIcon icon={faEnvelope} />
                            <input type="email" name="email" value={formData.email} onChange={handleInputChange} />
                          </div>
                        </div>
                        <div className="tp-field">
                          <label>{t('qualification')}</label>
                          <div className="tp-input-box">
                            <FontAwesomeIcon icon={faGraduationCap} />
                            <input type="text" name="qualification" value={formData.qualification} onChange={handleInputChange} />
                          </div>
                        </div>
                        <div className="tp-hint">
                          <FontAwesomeIcon icon={faBolt} />
                          <span>{t('profileSaveWarningHint')}</span>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}

                {activeTab === 'security' && (
                  <motion.div
                    key="security"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="tp-security-view"
                  >
                    {!isEditing ? (
                      <div className="tp-security-placeholder">
                        <div className="icon"><FontAwesomeIcon icon={faLock} /></div>
                        <h3>{t('protectYourAccount')}</h3>
                        <p>{t('passwordEncryptionNotice')}</p>
                      </div>
                    ) : (
                      <div className="tp-edit-form">
                        <div className="tp-field">
                          <label>{t('newPassword')}</label>
                          <div className="tp-input-box tp-input-box--pass">
                            <FontAwesomeIcon icon={faLock} />
                            <input
                              type={showPassword ? "text" : "password"}
                              name="password"
                              value={formData.password}
                              onChange={handleInputChange}
                              placeholder={t('leaveBlankToKeepUnchanged')}
                            />
                            <button type="button" onClick={() => setShowPassword(!showPassword)}>
                              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                            </button>
                          </div>
                          <small>{t('passwordMinLengthLimit')}</small>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.main>
      </div>

      <style jsx>{`
        .tp-root { font-family: "Zain", sans-serif; background: transparent; min-height: 80vh; padding: 20px 0; }
        .tp-container { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: 340px 1fr; gap: 24px; padding: 0 20px; }
        
        .tp-card { background: #fff; border-radius: 32px; box-shadow: 0 4px 20px rgba(0,0,0,0.03); border: 1px solid #fff; overflow: hidden; }
        
        /* Aside Identity */
        .tp-aside { display: flex; flex-direction: column; gap: 24px; }
        .tp-card--identity { text-align: center; padding-bottom: 32px; }
        .tp-card-header { height: 100px; background: #F8FAFC; position: relative; }
        .tp-subscription-badge { position: absolute; bottom: 12px; left: 12px; background: #EEF2FF; padding: 6px 14px; border-radius: 50px; color: #6366f1; font-weight: 900; font-size: 0.85rem; display: flex; align-items: center; gap: 8px; border: 1px solid #e0e7ff; }
        
        .tp-avatar-box { margin-top: -50px; position: relative; display: inline-flex; align-self: center; }
        .tp-avatar-ring { padding: 5px; background: #fff; border-radius: 50%; box-shadow: 0 10px 25px rgba(0,0,0,0.05); }
        .tp-avatar { width: 110px; height: 110px; border-radius: 50%; overflow: hidden; background: #f4f7fe; position: relative; }
        .tp-avatar img { width: 100%; height: 100%; object-fit: cover; }
        .tp-avatar-fallback { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 2.2rem; font-weight: 900; color: #6366f1; background: #eef2ff; }
        .tp-avatar-edit { position: absolute; inset: 0; background: rgba(0,0,0,0.5); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; cursor: pointer; opacity: 0; transition: 0.3s; }
        .tp-avatar:hover .tp-avatar-edit { opacity: 1; }
        .tp-status-dot { position: absolute; bottom: 8px; right: 8px; width: 18px; height: 18px; background: #10b981; border: 3px solid #fff; border-radius: 50%; }
        
        .tp-identity { margin-top: 16px; padding: 0 24px; }
        .tp-identity h2 { font-size: 1.6rem; font-weight: 900; color: #1b2559; margin: 0 0 10px; }
        .tp-tag { display: inline-flex; align-items: center; gap: 8px; background: #EEF2FF; padding: 6px 16px; border-radius: 50px; color: #6366f1; font-weight: 900; font-size: 0.9rem; }
        
        .tp-aside-actions { margin-top: 24px; padding: 0 24px; }
        .tp-btn { width: 100%; padding: 12px; border-radius: 12px; border: none; font-family: inherit; font-weight: 900; font-size: 1rem; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 10px; transition: 0.3s; }
        .tp-btn--primary { background: #6366f1; color: #fff; }
        .tp-btn--primary:hover { background: #4f46e5; box-shadow: 0 8px 20px rgba(99,102,241,0.25); }
        .tp-btn--success { background: #10b981; color: #fff; }
        .tp-btn--ghost { background: transparent; color: #64748b; margin-top: 8px; }
        .tp-btn--ghost:hover { background: #f1f5f9; }
        
        /* Stats */
        .tp-card--stats { padding: 20px; display: flex; flex-direction: column; gap: 16px; }
        .tp-stat-item { display: flex; align-items: center; gap: 16px; padding: 16px; background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 16px; }
        .tp-stat-item .icon { width: 44px; height: 44px; border-radius: 10px; background: #EEF2FF; color: #6366f1; display: flex; align-items: center; justify-content: center; font-size: 1.2rem; }
        .tp-stat-item .info { display: flex; flex-direction: column; }
        .tp-stat-item .label { color: #a3aed0; font-weight: 700; font-size: 0.85rem; }
        .tp-stat-item .value { font-weight: 900; color: #1b2559; font-size: 1rem; }
        .tp-stat-item .text-success { color: #10b981; }
        .tp-stat-item .text-amber { color: #6366f1; }
        
        /* Main Pane */
        .tp-main { min-width: 0; }
        .tp-card--main { min-height: 480px; }
        .tp-tabs-header { display: flex; gap: 8px; padding: 20px 24px 0; border-bottom: 1px solid #E2E8F0; }
        .tp-tab-btn { background: transparent; border: none; padding: 12px 20px; border-bottom: 3px solid transparent; font-family: inherit; font-weight: 900; font-size: 1rem; color: #a3aed0; cursor: pointer; display: flex; align-items: center; gap: 10px; transition: 0.3s; }
        .tp-tab-btn:hover { color: #6366f1; background: #f8faff; }
        .tp-tab-btn.is-active { color: #6366f1; border-bottom-color: #6366f1; }
        
        .tp-card-body { padding: 32px; }
        .tp-view-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
        
        /* Forms */
        .tp-edit-form { display: flex; flex-direction: column; gap: 20px; max-width: 600px; }
        .tp-field { display: flex; flex-direction: column; gap: 8px; }
        .tp-field label { font-weight: 900; color: #1b2559; font-size: 0.95rem; }
        .tp-input-box { position: relative; display: flex; align-items: center; }
        .tp-input-box svg { position: absolute; right: 16px; color: #a3aed0; font-size: 1rem; }
        .tp-input-box input { width: 100%; padding: 12px 48px 12px 16px; border-radius: 12px; border: 1px solid #E2E8F0; background: #F8FAFC; font-family: inherit; font-weight: 700; color: #1b2559; outline: none; transition: 0.3s; }
        .tp-input-box input:focus { border-color: #6366f1; background: #fff; box-shadow: 0 0 0 4px rgba(99,102,241,0.05); }
        .tp-input-box--pass input { padding-left: 48px; }
        .tp-input-box--pass button { position: absolute; left: 16px; background: transparent; border: none; color: #a3aed0; cursor: pointer; padding: 4px; }
        
        .tp-hint { display: flex; gap: 12px; background: #fefce8; border: 1.5px solid #fef3c7; padding: 16px; border-radius: 12px; color: #854d0e; }
        .tp-hint svg { font-size: 1.2rem; }
        .tp-hint span { font-weight: 800; font-size: 0.9rem; line-height: 1.5; }
        
        .tp-security-placeholder { text-align: center; padding: 60px 20px; border: 2px dashed #f4f7fe; border-radius: 24px; }
        .tp-security-placeholder .icon { font-size: 3rem; color: #eef2ff; margin-bottom: 20px; }
        .tp-security-placeholder h3 { font-size: 1.4rem; font-weight: 900; color: #1b2559; margin: 0 0 10px; }
        .tp-security-placeholder p { color: #a3aed0; font-weight: 800; max-width: 400px; margin: 0 auto; line-height: 1.6; }
        
        .tp-loader { height: 400px; display: flex; align-items: center; justify-content: center; }

        @media (max-width: 1024px) {
            .tp-container { grid-template-columns: 1fr; }
            .tp-aside { max-width: 500px; margin: 0 auto; width: 100%; }
        }
        @media (max-width: 600px) {
            .tp-view-grid { grid-template-columns: 1fr; }
            .tp-card-body { padding: 20px; }
        }
      `}</style>
    </div>
  );
};

const InfoItem = ({ icon, label, value }) => (
  <div className="tp-info-item">
    <div className="icon"><FontAwesomeIcon icon={icon} /></div>
    <div className="text">
      <span className="label">{label}</span>
      <p className="value">{value || '---'}</p>
    </div>
    <style jsx>{`
      .tp-info-item { display: flex; align-items: center; gap: 16px; padding: 20px; background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 18px; transition: 0.3s; }
      .tp-info-item:hover { border-color: #6366f1; background: #fff; transform: translateY(-3px); }
      .tp-info-item .icon { width: 44px; height: 44px; border-radius: 12px; background: #fff; color: #6366f1; display: flex; align-items: center; justify-content: center; font-size: 1.1rem; box-shadow: 0 4px 10px rgba(0,0,0,0.02); }
      .tp-info-item .text { display: flex; flex-direction: column; gap: 4px; }
      .tp-info-item .label { color: #a3aed0; font-weight: 700; font-size: 0.8rem; }
      .tp-info-item .value { font-weight: 900; color: #1b2559; font-size: 1.05rem; margin: 0; }
    `}</style>
  </div>
);

export default Profile;
